import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BadgeOutlined,
  CheckCircle,
  FolderOutlined,
  LocalOfferOutlined,
  RadioButtonUnchecked,
  Search,
} from '@mui/icons-material';
import LabelsService from '../../../../services/LabelsService';
import { BRAND_COLOR, BRAND_COLOR_DARK, PROFILE_COLOR } from './FormSection';

const normalizeText = (value) =>
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const MAX_PREVIEW_LABELS = 6;

const BlockTitle = ({ icon, title, description, count }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
    <Box sx={{ color: 'text.secondary', mt: 0.25 }}>{icon}</Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
        {count > 0 && (
          <Chip label={`${count} seleccionado${count === 1 ? '' : 's'}`} size="small" sx={{ ml: 1, height: 20, fontSize: 11 }} />
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Stack>
);

const SearchField = ({ value, onChange, placeholder }) => (
  <TextField
    size="small"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    sx={{ mb: 1.5, width: { xs: '100%', sm: 280 } }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search fontSize="small" />
        </InputAdornment>
      ),
    }}
  />
);

const GroupCard = ({ group, selected, onToggle }) => {
  const preview = group.labels.slice(0, MAX_PREVIEW_LABELS);
  const remaining = group.labels.length - preview.length;

  return (
    <Box
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? BRAND_COLOR : 'divider',
        bgcolor: selected ? `${BRAND_COLOR}0F` : '#fff',
        cursor: 'pointer',
        transition: 'all .15s',
        height: '100%',
        outline: 'none',
        '&:hover': { borderColor: BRAND_COLOR, boxShadow: `0 2px 10px ${BRAND_COLOR}26` },
        '&:focus-visible': { boxShadow: `0 0 0 3px ${BRAND_COLOR}4D` },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {selected ? (
          <CheckCircle sx={{ color: BRAND_COLOR }} />
        ) : (
          <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />
        )}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography fontWeight={700} noWrap>
            {group.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }} noWrap>
            {group.description || `${group.labels.length} etiqueta${group.labels.length === 1 ? '' : 's'}`}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {preview.map((label) => (
              <Chip
                key={label.id}
                label={label.name}
                size="small"
                sx={{ height: 22, fontSize: 11, bgcolor: selected ? `${BRAND_COLOR}26` : 'grey.100' }}
              />
            ))}
            {remaining > 0 && (
              <Chip label={`+${remaining}`} size="small" variant="outlined" sx={{ height: 22, fontSize: 11 }} />
            )}
            {group.labels.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                Grupo sin etiquetas
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const CourseAudienceSection = ({
  audienceTargets,
  onAudienceChange,
  selectedGroupIds,
  onGroupsChange,
}) => {
  const [labels, setLabels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [labelSearch, setLabelSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [labelsResponse, groupsResponse] = await Promise.all([
          LabelsService.getAllLabels(),
          LabelsService.getLabelGroupsOverview().catch(() => []),
        ]);
        if (cancelled) return;
        setLabels(labelsResponse || []);
        setGroups(groupsResponse || []);
      } catch (error) {
        if (!cancelled) setLoadError('No se pudieron cargar las etiquetas.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedGroupSet = useMemo(() => new Set(selectedGroupIds || []), [selectedGroupIds]);

  // labelId -> nombres de los grupos seleccionados que la incluyen
  const labelsFromGroups = useMemo(() => {
    const map = new Map();
    groups
      .filter((group) => selectedGroupSet.has(group.id))
      .forEach((group) => {
        group.labels.forEach((label) => {
          map.set(label.id, [...(map.get(label.id) || []), group.name]);
        });
      });
    return map;
  }, [groups, selectedGroupSet]);

  const profileLabels = useMemo(
    () => labels.filter((label) => label.isProfile).sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [labels]
  );

  const interestLabels = useMemo(() => {
    const term = normalizeText(labelSearch);
    return labels
      .filter((label) => !label.isProfile)
      .filter((label) => !term || normalizeText(label.name).includes(term))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [labels, labelSearch]);

  const filteredGroups = useMemo(() => {
    const term = normalizeText(groupSearch);
    return term ? groups.filter((group) => normalizeText(group.name).includes(term)) : groups;
  }, [groups, groupSearch]);

  const manualCount = Object.values(audienceTargets || {}).filter(Boolean).length;
  const selectedProfileCount = profileLabels.filter((label) => audienceTargets?.[label.id]).length;
  const selectedInterestCount = manualCount - selectedProfileCount;

  const effectiveLabelCount = useMemo(() => {
    const ids = new Set(labelsFromGroups.keys());
    Object.entries(audienceTargets || {}).forEach(([id, selected]) => {
      if (selected) ids.add(Number(id));
    });
    return ids.size;
  }, [audienceTargets, labelsFromGroups]);

  const toggleGroup = (groupId) => {
    const next = selectedGroupSet.has(groupId)
      ? (selectedGroupIds || []).filter((id) => id !== groupId)
      : [...(selectedGroupIds || []), groupId];
    onGroupsChange(next);
  };

  if (isLoading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">Cargando etiquetas...</Typography>
      </Stack>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Alert
        icon={<LocalOfferOutlined />}
        severity={effectiveLabelCount > 0 ? 'success' : 'info'}
        sx={{ borderRadius: 2 }}
      >
        {effectiveLabelCount > 0 ? (
          <>
            El curso se recomendará a los usuarios que coincidan con{' '}
            <strong>
              {effectiveLabelCount} etiqueta{effectiveLabelCount === 1 ? '' : 's'}
            </strong>
            {selectedGroupSet.size > 0 &&
              ` (incluye ${selectedGroupSet.size} grupo${selectedGroupSet.size === 1 ? '' : 's'})`}
            .
          </>
        ) : (
          'Selecciona grupos, perfiles o etiquetas para definir a quién va dirigido el curso.'
        )}
      </Alert>

      <Box>
        <BlockTitle
          icon={<FolderOutlined fontSize="small" />}
          title="Grupos de etiquetas"
          description="Asocia grupos completos. Si luego se modifica un grupo, el curso se actualiza automáticamente."
          count={selectedGroupSet.size}
        />
        {groups.length > 6 && (
          <SearchField value={groupSearch} onChange={setGroupSearch} placeholder="Buscar grupo..." />
        )}
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay grupos de etiquetas creados. Puedes crearlos en el gestor de etiquetas.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 1.5,
              maxHeight: 420,
              overflowY: 'auto',
              p: 0.5,
            }}
          >
            {filteredGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                selected={selectedGroupSet.has(group.id)}
                onToggle={() => toggleGroup(group.id)}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box>
        <BlockTitle
          icon={<BadgeOutlined fontSize="small" />}
          title="Perfiles"
          description="Rol o especialidad principal de los usuarios a quienes va dirigido."
          count={selectedProfileCount}
        />
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {profileLabels.map((label) => {
            const selected = Boolean(audienceTargets?.[label.id]);
            return (
              <Chip
                key={label.id}
                label={label.name}
                clickable
                onClick={() => onAudienceChange(label.id, !selected)}
                icon={selected ? <CheckCircle /> : undefined}
                variant={selected ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 500,
                  borderColor: `${PROFILE_COLOR}80`,
                  color: selected ? '#fff' : PROFILE_COLOR,
                  bgcolor: selected ? PROFILE_COLOR : 'transparent',
                  '& .MuiChip-icon': { color: '#fff' },
                  '&:hover': { bgcolor: selected ? '#6a46b0' : `${PROFILE_COLOR}14` },
                  '&.MuiChip-clickable:hover': { bgcolor: selected ? '#6a46b0' : `${PROFILE_COLOR}14` },
                }}
              />
            );
          })}
          {profileLabels.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No hay etiquetas de perfil registradas.
            </Typography>
          )}
        </Stack>
      </Box>

      <Box>
        <BlockTitle
          icon={<LocalOfferOutlined fontSize="small" />}
          title="Etiquetas de interés"
          description="Selecciona etiquetas individuales. Las incluidas por un grupo aparecen marcadas automáticamente."
          count={selectedInterestCount}
        />
        <SearchField value={labelSearch} onChange={setLabelSearch} placeholder="Buscar etiqueta..." />
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ maxHeight: 260, overflowY: 'auto', p: 0.5 }}>
          {interestLabels.map((label) => {
            const fromGroups = labelsFromGroups.get(label.id);
            const selectedManually = Boolean(audienceTargets?.[label.id]);

            if (fromGroups && !selectedManually) {
              return (
                <Tooltip key={label.id} title={`Incluida por: ${fromGroups.join(', ')}`} arrow>
                  <Chip
                    label={label.name}
                    icon={<FolderOutlined />}
                    sx={{
                      fontWeight: 500,
                      bgcolor: `${BRAND_COLOR}26`,
                      color: BRAND_COLOR_DARK,
                      '& .MuiChip-icon': { color: BRAND_COLOR_DARK },
                    }}
                  />
                </Tooltip>
              );
            }

            return (
              <Chip
                key={label.id}
                label={label.name}
                clickable
                onClick={() => onAudienceChange(label.id, !selectedManually)}
                icon={selectedManually ? <CheckCircle /> : undefined}
                variant={selectedManually ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 500,
                  borderColor: `${BRAND_COLOR}80`,
                  color: selectedManually ? '#fff' : BRAND_COLOR_DARK,
                  bgcolor: selectedManually ? BRAND_COLOR : 'transparent',
                  '& .MuiChip-icon': { color: '#fff' },
                  '&.MuiChip-clickable:hover': { bgcolor: selectedManually ? BRAND_COLOR_DARK : `${BRAND_COLOR}14` },
                }}
              />
            );
          })}
          {interestLabels.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {labelSearch ? 'Ninguna etiqueta coincide con la búsqueda.' : 'No hay etiquetas de interés registradas.'}
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default CourseAudienceSection;
